import Page16Icon from '@atlaskit/icon-object/glyph/page/24';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { getCompactSelectStyles } from '@databases/atlaskit/compact-select-styles';
import {
  AsyncSelect,
  type FormatOptionLabelMeta,
  type StylesConfig
} from '@databases/atlaskit/Select';
import { searchContent } from '@databases/connections/content/contentSearch';
import { getCloudId } from '@databases/database-state';
import DatabaseIcon from '@databases/icons/DatabaseIcon';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { useResolvedReference } from '@databases/redux/resolved-reference';
import { type Content, type ContentReference } from '@databases/types/confluence';
import { createContentARI } from '@databases/utils/ariHelper';
import { getPageKey } from '@databases/utils/confluence/utils';
import { loadContentById } from '@databases/utils/redux/content/actions';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { matchViewPageOrBlogpostPath } from '@databases/utils/urls';
import debounce from 'debounce-promise';
import React, {
  type FunctionComponent,
  type ReactNode,
  useCallback,
  useMemo
} from 'react';
import { useIntl } from 'react-intl-next';

import ParentPickerOption from './ParentPickerOption.tsx';
import { type ExtendedPageReference, processSearchContentData } from './utils.ts';

type SourceDatabaseOption = {
  type: 'database';
  name: string;
  contentId: string;
  spaceKey: string;
};

// Exact URL matches resolve to Content<'page'>; search results use ExtendedPageReference.
type Option = Content<'page'> | ExtendedPageReference | SourceDatabaseOption;

function isPageReference(
  value: Content<'page'> | ExtendedPageReference
): value is ExtendedPageReference {
  return 'contentTitle' in value;
}
const getDatabaseKey = (db: SourceDatabaseOption): string => {
  return `database:${db.spaceKey.toLowerCase()}:${db.name.toLowerCase()}`;
};

const contentToExtendedPageReference = (
  value: Content<'page'>
): ExtendedPageReference | undefined => {
  const spaceKey = value.space?.key;
  if (!spaceKey) {
    return undefined;
  }

  return {
    type: value.type,
    spaceKey,
    contentTitle: value.title,
    contentId: value.id,
    ari: createContentARI(value.type, value.id)
  };
};

const getOptionValue = (value: Option) => {
  return value.type === 'database' ? getDatabaseKey(value) : getPageKey(value);
};

const styles: StylesConfig<Option> = {
  menu: (base) => ({
    ...base,
    maxWidth: '100%',
    minWidth: '100%'
  }),
  singleValue: (base) => ({
    ...base,
    padding: token('space.025', '2px'), // Give space to preview box shadow
    // @remark: Disabled as part of https://bitbucket.org/atlassian/canvas/pull-requests/27504/overview,
    // this should be re-enabled as soon as feasible
    // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
    lineHeight: '20px',
    pointerEvents: 'none' // Prevent interaction with preview
  })
};
const compactBaseStyles = getCompactSelectStyles<Option>();
const compactStyles: StylesConfig<Option> = {
  ...styles,
  ...compactBaseStyles,
  valueContainer: (base, state) => ({
    ...compactBaseStyles.valueContainer?.(base, state),
    alignItems: 'center'
  })
};

export interface ParentPickerProps {
  contentId: string;
  fieldId: string;
  value: ContentReference | undefined;
  handleSetParentPage: (value: ContentReference | undefined) => void;
  isPending?: boolean;
  isInvalid?: boolean;
  isLoading?: boolean;
  isSourceOption?: boolean;
  handleSetIsParentPageSourceDatabase: (value: boolean) => void;
  inputId?: string;
  isCompact?: boolean;
}

const ParentPicker: FunctionComponent<ParentPickerProps> = ({
  contentId,
  fieldId,
  value,
  handleSetParentPage,
  isPending,
  isInvalid,
  isLoading,
  isSourceOption,
  handleSetIsParentPageSourceDatabase,
  inputId,
  isCompact
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();

  const database = defaultStoreUtils.useSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId
    });
    return database;
  });

  const resolvedContentRef = useResolvedReference(value);

  const resolvedValue = useMemo(() => {
    if (resolvedContentRef && resolvedContentRef.type !== 'blogpost') {
      return [resolvedContentRef];
    } else {
      return [];
    }
  }, [resolvedContentRef]);

  const baseUrl = defaultStoreUtils.useSelector(selectBaseUrl);
  const handleChange = useCallback(
    (val: ReadonlyArray<Option> | Option | null) => {
      let parentId: string | null;
      if (!val) {
        handleSetIsParentPageSourceDatabase(false);
        handleSetParentPage(undefined);
        parentId = null;
      } else if ('type' in val && val.type === 'database') {
        handleSetIsParentPageSourceDatabase(true);
        handleSetParentPage(undefined);
        parentId = contentId;
      } else {
        const arr = Array.isArray(val) ? val : val ? [val] : [];
        const value = arr
          .map((v: Content<'page'> | ExtendedPageReference) =>
            isPageReference(v) ? v : contentToExtendedPageReference(v)
          )
          .find((v) => v !== undefined);
        handleSetParentPage(value ? { contentId: value.contentId } : undefined);
        handleSetIsParentPageSourceDatabase(false);
        parentId = value?.contentId ?? null;
      }
      client.sendPageCreationFieldSettingEditedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        fieldId,
        fieldSetting: 'parentItem',
        value: parentId
      });
    },
    [client, contentId, fieldId, handleSetIsParentPageSourceDatabase, handleSetParentPage]
  );

  const getContentBySearchQuery = useCallback(
    async (query: string): Promise<ExtendedPageReference[]> => {
      const data = await searchContent(query, getCloudId() ?? '');

      return processSearchContentData(data);
    },
    []
  );

  const getRecentlyViewedContent = useMemo(async (): Promise<ExtendedPageReference[]> => {
    const data = await searchContent('', getCloudId() ?? '');

    return processSearchContentData(data);
  }, []);

  const formatOptionLabel = useCallback(
    (value: Option, meta: FormatOptionLabelMeta<Option>): ReactNode => {
      const selectedValues: Array<string> = (
        Array.isArray(meta.selectValue)
          ? meta.selectValue
          : meta.selectValue
            ? [meta.selectValue]
            : []
      ).map((val) => getOptionValue(val));

      const isSelected = selectedValues.includes(getOptionValue(value));
      if (value.type === 'database') {
        return (
          <ParentPickerOption
            IconComponent={(iconProps) => <DatabaseIcon {...iconProps} size="medium" />}
            valueName={value.name}
            spaceName={database.space.name}
            context={meta.context}
            isSelected={isSelected}
          />
        );
      } else {
        const title = isPageReference(value) ? value.contentTitle : value.title;
        const spaceName = isPageReference(value)
          ? value.metadata?.spaceName
          : value.space?.name;

        return (
          <ParentPickerOption
            IconComponent={(iconProps) => <Page16Icon {...iconProps} />}
            valueName={title}
            spaceName={`${spaceName ?? ''}`}
            context={meta.context}
          />
        );
      }
    },
    [database.space.name]
  );

  const loadOptions = useCallback(
    async (
      inputValue: string
    ): Promise<
      | {
          label: string;
          options: Option[];
        }[]
      | (Content<'page'> | ExtendedPageReference)[]
    > => {
      const inputValueTrimmed = inputValue.trim();
      const contentMatch = inputValueTrimmed.startsWith(baseUrl)
        ? matchViewPageOrBlogpostPath(inputValueTrimmed)
        : undefined;

      const [recentlyViewedPages, searchResults, contentMatchResolved] =
        await Promise.all([
          inputValueTrimmed ? [] : getRecentlyViewedContent,
          inputValueTrimmed ? getContentBySearchQuery(inputValueTrimmed) : [],
          contentMatch ? dispatch(loadContentById(contentMatch?.contentId)) : undefined
        ]);
      const pageMatchResolved =
        contentMatchResolved?.type === 'page' ? contentMatchResolved : undefined;
      const results = inputValueTrimmed ? searchResults : recentlyViewedPages;

      if (inputValueTrimmed) {
        return [
          ...(pageMatchResolved ? [pageMatchResolved] : []),
          ...(!contentMatch ? results : [])
        ];
      }

      return [
        {
          label: formatMessage(
            messages['field-settings.parent-picker-source-database-label']
          ),
          options: [
            {
              type: 'database',
              name: database.name,
              contentId: database.contentId,
              spaceKey: database.space.key
            }
          ]
        },
        {
          label: formatMessage(
            messages['field-settings.parent-picker-recently-viewed-pages-label']
          ),
          options: results
        }
      ];
    },
    [
      baseUrl,
      database.contentId,
      database.name,
      database.space.key,
      dispatch,
      formatMessage,
      getContentBySearchQuery,
      getRecentlyViewedContent
    ]
  );

  const debouncedLoadOptions = useMemo(() => debounce(loadOptions, 300), [loadOptions]);

  return (
    <AsyncSelect<Option>
      classNamePrefix="parent-picker"
      loadOptions={debouncedLoadOptions}
      value={
        isSourceOption
          ? {
              type: 'database',
              name: database.name,
              contentId: database.contentId,
              spaceKey: database.space.key
            }
          : (resolvedValue ?? null)
      }
      onChange={handleChange}
      hideSelectedOptions={!isSourceOption}
      formatOptionLabel={formatOptionLabel}
      getOptionValue={getOptionValue}
      isLoading={isLoading || isPending || (resolvedValue.length === 0 && !!value)}
      loadingMessage={() =>
        formatMessage(messages['field-settings.select-picker-loading-text'])
      }
      inputId={inputId}
      isClearable
      isInvalid={isInvalid}
      spacing={isCompact ? 'compact' : undefined}
      styles={isCompact ? compactStyles : styles}
      placeholder={formatMessage(
        messages['field-settings.type-page-link-parent-item-placeholder']
      )}
    />
  );
};

export default ParentPicker;
